// Offertförfrågan för affärsområdet Foto.
// Går till huvudinkorgen så att
// ingen förfrågan tappas bort. Inga priser hanteras här — allt offereras.

import { RESPONSE_PROMISE_SV } from "@/content/response-times";
import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { z } from "zod";
import { persistLead, captureBlockedLead } from "@/lib/lead-fallback.server";
import { verifyRecaptcha } from "@/lib/recaptcha.server";
import {
  confirmationEmail,
  internalLeadEmail,
  LEAD_INBOX,
  LEAD_NOTIFY,
  sendEmail,
} from "@/lib/email/resend.server";
import { checkRateLimit, getClientIp } from "@/lib/rate-limit.server";

export const PHOTO_CONTACT_EMAIL = LEAD_INBOX;

export const FOTO_ASSIGNMENT_VALUES = [
  "foretagsportratt",
  "kampanjfoto",
  "eventfoto",
  "produktfoto",
  "studiofoto",
  "foto-och-film",
  "vet-inte",
] as const;

export const FOTO_ASSIGNMENT_LABELS: Record<
  (typeof FOTO_ASSIGNMENT_VALUES)[number],
  string
> = {
  foretagsportratt: "Företagsporträtt / headshots",
  kampanjfoto: "Kampanj- och varumärkesbilder",
  eventfoto: "Event, konferens eller mässa",
  produktfoto: "Produktfoto",
  studiofoto: "Studiofotografering",
  "foto-och-film": "Foto och film i samma uppdrag",
  "vet-inte": "Vet inte än",
};

const Schema = z.object({
  name: z.string().trim().max(100).optional().default(""),
  email: z.string().trim().email("Ogiltig e-postadress").max(255),
  phone: z.string().trim().max(40).optional().default(""),
  company: z.string().trim().max(150).optional().default(""),
  assignment: z.enum(FOTO_ASSIGNMENT_VALUES).default("vet-inte"),
  scope: z.string().trim().max(120).optional().default(""),
  city: z.string().trim().max(80).optional().default(""),
  date: z.string().trim().max(60).optional().default(""),
  message: z.string().trim().max(2000).optional().default(""),
  source_path: z.string().trim().max(255).optional().default(""),
  // Valfri referensbild (base64) som skickas med till Fenny.
  attachment: z
    .object({
      filename: z.string().trim().min(1).max(120),
      contentType: z
        .enum(["image/jpeg", "image/png", "image/webp", "image/heic", "application/pdf"]),
      // ~4 MB base64 ≈ 3 MB fil
      data: z.string().max(4_200_000),
    })
    .nullable()
    .optional(),
  token: z.string().min(10).max(4000),
  session_id: z.string().uuid().nullable().optional(),
  visitor_id: z.string().uuid().nullable().optional(),
});

export const submitFotoQuote = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => Schema.parse(input))
  .handler(async ({ data }) => {
    const ip = getClientIp(getRequest());
    if (
      !checkRateLimit({
        scope: "foto-quote",
        keys: [ip, data.email],
        limit: 5,
        windowMs: 60 * 60 * 1000,
      })
    ) {
      await captureBlockedLead({ flow: "foto-quote", reason: "rate-limit",
        email: data.email,
        name: data.name || null,
        company: data.company || null,
        phone: data.phone || null,
        message: data.message || null,
        source_path: data.source_path || null,
        session_id: data.session_id ?? null,
        visitor_id: data.visitor_id ?? null,
        extra: { assignment: data.assignment, city: data.city, date: data.date, scope: data.scope },
      });
      return { ok: false as const, error: "För många försök. Försök igen om en stund." };
    }

    const captcha = await verifyRecaptcha(data.token);
    if (captcha.action === "block") {
      await captureBlockedLead({
        flow: "foto-quote",
        reason: "recaptcha",
        email: data.email,
        name: data.name || null,
        company: data.company || null,
        phone: data.phone || null,
        message: data.message || null,
        source_path: data.source_path || null,
        session_id: data.session_id ?? null,
        visitor_id: data.visitor_id ?? null,
        extra: { assignment: data.assignment, city: data.city, date: data.date, scope: data.scope },
      });
      return { ok: false as const, error: "Verifieringen misslyckades. Försök igen." };
    }

    const assignmentLabel = FOTO_ASSIGNMENT_LABELS[data.assignment];
    const displayName = data.name?.trim() || data.email;

    const summary = [
      `Uppdrag: ${assignmentLabel}`,
      data.scope ? `Omfattning: ${data.scope}` : null,
      data.city ? `Ort: ${data.city}` : null,
      data.date ? `Önskat datum: ${data.date}` : null,
      data.phone ? `Telefon: ${data.phone}` : null,
      data.message ? `\n${data.message}` : null,
    ]
      .filter(Boolean)
      .join("\n");

    try {
      await persistLead("contact_submissions", {
        name: data.name || "",
        email: data.email,
        company: data.company || null,
        intent: `foto:${data.assignment}`,
        message: summary,
        source_path: data.source_path || null,
        session_id: data.session_id ?? null,
        visitor_id: data.visitor_id ?? null,
      }, {
        source: "foto-quote",
        email: data.email,
        name: data.name || null,
        company: data.company || null,
        source_path: data.source_path || null,
      });
    } catch (e) {
      console.error("[foto-quote] insert unexpected:", e);
    }

    const internal = internalLeadEmail({
      subject: `Offertförfrågan foto (${assignmentLabel}) — ${displayName}`,
      heading: "Ny offertförfrågan — Foto",
      fields: {
        Namn: data.name || "—",
        "E-post": data.email,
        Telefon: data.phone || "—",
        Företag: data.company || "—",
        Uppdrag: assignmentLabel,
        Omfattning: data.scope || "—",
        Ort: data.city || "—",
        "Önskat datum": data.date || "—",
        Meddelande: data.message || "—",
        Bilaga: data.attachment ? data.attachment.filename : "—",
        Sida: data.source_path || "—",
      },
    });

    const confirm = confirmationEmail({
      name: displayName,
      subject: "Tack — din offertförfrågan är mottagen",
      heading: "Vi har tagit emot din förfrågan om foto",
      intro:
        `Tack för att du hörde av dig till Fenny. Vi går igenom din förfrågan och återkommer med ett förslag och en offert. ${RESPONSE_PROMISE_SV}`,
      body: `Du frågade om: ${assignmentLabel}. Vi arbetar alltid med offert per uppdrag — priset sätts efter omfattning, antal bilder och användningsområde, inte efter en fast prislista.`,
    });

    await Promise.allSettled([
      sendEmail({
        // Till ordinarie leadmottagare.
        to: LEAD_NOTIFY,
        subject: internal.subject,
        html: internal.html,
        replyTo: data.email,
        ...(data.attachment
          ? {
              attachments: [
                {
                  filename: data.attachment.filename,
                  content: data.attachment.data,
                  contentType: data.attachment.contentType,
                },
              ],
            }
          : {}),
        logContext: { kind: "internal_lead", leadSource: "foto", leadEmail: data.email },
      }),
      sendEmail({
        to: data.email,
        subject: confirm.subject,
        html: confirm.html,
        replyTo: PHOTO_CONTACT_EMAIL,
        logContext: { kind: "lead_confirmation", leadSource: "foto", leadEmail: data.email },
      }),
    ]);

    return { ok: true as const };
  });

export const getFotoRecaptchaSiteKey = createServerFn({ method: "GET" }).handler(
  async () => ({ siteKey: process.env.RECAPTCHA_SITE_KEY ?? "" }),
);
